import React from "react"
import { useController, useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"

import type { ProviderSelection } from "@/modules/admin/credentials/provider-selection"
import { providerNamesMatch } from "@/modules/admin/credentials/provider-selection"
import { TextField } from "@/nzds/inputs/text-field/text-field"
import type { ResourceProvider } from "@/services/credential"

import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete"

const filterProviderOptions = createFilterOptions<ProviderSelection>()

interface ProviderFieldValues {
  provider: ProviderSelection | null
}

interface ProviderSelectFieldProps {
  disabled?: boolean
  providers: ResourceProvider[]
}

const isProviderOptionEqual = (
  left: ProviderSelection,
  right: ProviderSelection
): boolean => {
  if (left.kind === "pending" && right.kind === "pending") {
    return providerNamesMatch(left.name, right.name)
  }

  if (left.kind === "existing" && right.kind === "existing") {
    return left.id === right.id
  }

  return false
}

export const ProviderSelectField: React.FC<ProviderSelectFieldProps> = ({
  disabled = false,
  providers,
}) => {
  const { t } = useTranslation("common")
  const form = useFormContext<ProviderFieldValues>()
  const {
    field: { onBlur, onChange, ref, value },
    fieldState,
  } = useController({
    control: form.control,
    name: "provider",
  })

  const catalogOptions: ProviderSelection[] = providers.map((provider) => ({
    id: provider.id,
    kind: "existing",
    name: provider.name,
  }))

  return (
    <Autocomplete
      clearOnBlur
      disabled={disabled}
      filterOptions={(options, params) => {
        const filtered = filterProviderOptions(options, params)
        const trimmedInput = params.inputValue.trim()

        if (trimmedInput === "") {
          return filtered
        }

        const exactMatchExists = options.some((option) =>
          providerNamesMatch(option.name, trimmedInput)
        )

        if (!exactMatchExists) {
          filtered.push({ kind: "pending", name: trimmedInput })
        }

        return filtered
      }}
      getOptionLabel={(option) => option.name}
      handleHomeEndKeys
      isOptionEqualToValue={isProviderOptionEqual}
      onBlur={onBlur}
      onChange={(_event, newValue) => {
        onChange(newValue)
      }}
      options={catalogOptions}
      renderInput={(params) => (
        <TextField
          {...params}
          error={Boolean(fieldState.error)}
          helperText={
            fieldState.error?.message ??
            t("admin.credentialsUi.providerHelperText")
          }
          inputRef={ref}
          label={t("fields.utilityCredential.title")}
          name="provider"
          required
          size="small"
          variant="stacked"
        />
      )}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props
        const label =
          option.kind === "pending"
            ? t("admin.credentialsUi.addProviderOption", { name: option.name })
            : option.name

        return (
          <li key={key} {...optionProps}>
            {label}
          </li>
        )
      }}
      selectOnFocus
      size="small"
      value={value}
    />
  )
}
